import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import { IGlobalLookupRecord } from 'common/models/SearchDefinitions';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GlobalLookup } from 'services/SearchService';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import { RecentRecordItem } from 'common/models/CustomApplicationContextType';
import { RecentRecordsSettingName } from 'util/LocalStorageUtils';
import Divider from '@mui/material/Divider';
import BadgeIcon from '@mui/icons-material/Badge';
import DomainIcon from '@mui/icons-material/Domain';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CampaignIcon from '@mui/icons-material/Campaign';
import AssistantDirectionIcon from '@mui/icons-material/AssistantDirection';
import Avatar from '@mui/material/Avatar';

interface Props {
  open: boolean;
  closeHandler: () => void;
}

const startAdornment = (
  <InputAdornment position="start">
    <SearchIcon />
  </InputAdornment>
);

const recordLink = (entityId: number, recordId: number): string => {
  switch (entityId) {
    case 1: // Client
      return `/clients/${recordId}`;
    case 2: // Contact
      return `/contacts/${recordId}`;
    case 3: // Candidate
      return `/candidates/${recordId}`;
    case 4: // Job
      return `/jobs/${recordId}`;
    case 5: // Placement
      return `/placements/${recordId}`;
    case 8: // Opportunity
      return `/opportunities/${recordId}`;
    case 9: // Site
      return `/sites/${recordId}`;
    case 15: // Advert
      return `/adverts/${recordId}`;
    default:
      return '';
  }
};

const recentRecordsSecondaryText = (rec: RecentRecordItem): JSX.Element => {
  let separator = '';
  let firstValue = '';
  let secondValue = '';
  const bJobTitle = Boolean(rec.extJobTitle);
  const bEmployer = Boolean(rec.extEmployer);
  const bStatusName = Boolean(rec.extStatus);
  const bTypeName = Boolean(rec.extType);
  const bRecordName = Boolean(rec.title);
  let firstLine = '';
  let secondLine = '';

  switch (rec.entityId) {
    case 1: // Client
      if (bStatusName) {
        return <>{rec.extStatus}</>;
      }
      return <></>;
    case 2: // Contact
    case 3: // Candidate
      separator = bJobTitle && bEmployer ? ' @ ' : separator;
      firstValue = bJobTitle ? rec.extJobTitle : '';
      secondValue = bEmployer ? rec.extEmployer : '';
      return (
        <div style={{ flexWrap: 'wrap', maxWidth: '390px' }}>
          {firstValue + separator + secondValue}
        </div>
      );
    case 4: // Job
      separator = bEmployer && bStatusName ? ' - ' : separator;
      firstValue = bEmployer ? rec.extEmployer : '';
      secondValue = bStatusName ? rec.extStatus : '';
      firstLine =
        firstValue === '' && secondValue === '' ? '' : firstValue + separator + secondValue;
      secondLine = bTypeName ? rec.extType : '';
      if (firstLine === '' || secondLine === '') return <>{firstLine + secondLine}</>;
      return (
        <>
          {firstLine}
          <br />
          {secondLine}
        </>
      );
    case 5: // Placement
      separator = bRecordName && bEmployer ? ' @ ' : separator;
      firstValue = bRecordName ? rec.title : '';
      secondValue = bEmployer ? rec.extEmployer : '';
      firstLine =
        firstValue === '' && secondValue === '' ? '' : firstValue + separator + secondValue;
      secondLine = bTypeName ? rec.extType : '';
      if (firstLine === '' || secondLine === '') return <>{firstLine + secondLine}</>;
      return (
        <div style={{ flexWrap: 'wrap', maxWidth: '390px' }}>
          {firstLine}
          <br />
          {secondLine}
        </div>
      );
    case 8: // Opportunity
    case 9: // Site
    case 15: // Advert
      separator = bEmployer && bStatusName ? ' - ' : separator;
      firstValue = bEmployer ? rec.extEmployer : '';
      secondValue = bStatusName ? rec.extStatus : '';
      return (
        <div style={{ flexWrap: 'wrap', maxWidth: '390px' }}>
          {firstValue + separator + secondValue}
        </div>
      );
    default:
      return <></>;
  }
};

const entityName = (entityId: number): string => {
  switch (entityId) {
    case 1:
      return 'Client';
    case 2:
      return 'Contact';
    case 3:
      return 'Candidate';
    case 4:
      return 'Job';
    case 5:
      return 'Placement';
    case 8:
      return 'Opportunity';
    case 9:
      return 'Site';
    case 15:
      return 'Advert';
    default:
      return '';
  }
};

const entityIcon = (entityId: number): ReactElement | null => {
  switch (entityId) {
    case 1:
      return <DomainIcon sx={{ width: '18px', height: '18px' }} />;
    case 2:
      return <BadgeIcon sx={{ width: '18px', height: '18px' }} />;
    case 3:
      return <PersonIcon sx={{ width: '18px', height: '18px' }} />;
    case 4:
      return <WorkIcon sx={{ width: '18px', height: '18px' }} />;
    case 5:
      return <DoneAllIcon sx={{ width: '18px', height: '18px' }} />;
    case 8:
      return <CreditCardIcon sx={{ width: '18px', height: '18px' }} />;
    case 9:
      return <AssistantDirectionIcon sx={{ width: '18px', height: '18px' }} />;
    case 15:
      return <CampaignIcon sx={{ width: '18px', height: '18px' }} />;
    default:
      return null;
  }
};

const secondaryText = ({
  recordType,
  statusName,
  jobTitle,
  employer,
  typeName,
  recordName,
}: IGlobalLookupRecord): JSX.Element => {
  let separator = '';
  let firstValue = '';
  let secondValue = '';
  const bJobTitle = Boolean(jobTitle);
  const bEmployer = Boolean(employer);
  const bStatusName = Boolean(statusName);
  const bTypeName = Boolean(typeName);
  const bRecordName = Boolean(recordName);
  let firstLine = '';
  let secondLine = '';
  switch (recordType) {
    case 1: // Client
      if (bStatusName) {
        return <>{statusName}</>;
      }
      return <></>;
    case 2: // Contact
    case 3: // Candidate
      separator = bJobTitle && bEmployer ? ' @ ' : separator;
      firstValue = bJobTitle ? jobTitle : '';
      secondValue = bEmployer ? employer : '';
      return (
        <div style={{ flexWrap: 'wrap', maxWidth: '390px' }}>
          {firstValue + separator + secondValue}
        </div>
      );
    case 4: // Job
      separator = bEmployer && bStatusName ? ' - ' : separator;
      firstValue = bEmployer ? employer : '';
      secondValue = bStatusName ? statusName : '';
      firstLine =
        firstValue === '' && secondValue === '' ? '' : firstValue + separator + secondValue;
      secondLine = bTypeName ? typeName : '';
      if (firstLine === '' || secondLine === '') return <>{firstLine + secondLine}</>;
      return (
        <>
          {firstLine}
          <br />
          {secondLine}
        </>
      );
    case 5: // Placement
      separator = bRecordName && bEmployer ? ' @ ' : separator;
      firstValue = bRecordName ? recordName : '';
      secondValue = bEmployer ? employer : '';
      firstLine =
        firstValue === '' && secondValue === '' ? '' : firstValue + separator + secondValue;
      secondLine = bTypeName ? typeName : '';
      if (firstLine === '' || secondLine === '') return <>{firstLine + secondLine}</>;
      return (
        <div style={{ flexWrap: 'wrap', maxWidth: '390px' }}>
          {firstLine}
          <br />
          {secondLine}
        </div>
      );
    case 8: // Opportunity
    case 9: // Site
    case 15: // Advert
      separator = bEmployer && bStatusName ? ' - ' : separator;
      firstValue = bEmployer ? employer : '';
      secondValue = bStatusName ? statusName : '';
      return <>{firstValue + separator + secondValue}</>;
    default:
      return <></>;
  }
};

export default function UnifiedSearchDialog({ open, closeHandler }: Props) {
  const [term, setTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<IGlobalLookupRecord[]>([]);
  const [recentRecords, setRecentRecords] = useState<RecentRecordItem[]>([]);
  const [isNoResultsFound, setIsNoResultsFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setTerm('');
      setResults([]);
      setRecentRecords([]);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const stored = localStorage.getItem(RecentRecordsSettingName);
      if (stored) {
        const parsed = JSON.parse(stored) as RecentRecordItem[];
        setRecentRecords(parsed);
      }
    }
  }, [open]);

  useEffect(() => {
    setIsNoResultsFound(false);
    const delaySearch = setTimeout(async () => {
      if (term.length > 2) {
        setIsLoading(true);
        const data = await GlobalLookup(term);
        if (data && data.lookup === term) {
          setResults(data.results);
          setIsNoResultsFound(data.results.length === 0);
        }
        setIsLoading(false);
      } else setResults([]);
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [term]);

  const onSubmitHandler = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      const path = `/home/lookup?q=${encodeURIComponent(term)}`;
      navigate(path);
      closeHandler();
    },
    [term, navigate, closeHandler]
  );

  const endAdornment = useMemo(() => {
    if (!isLoading) return undefined;
    return (
      <InputAdornment position="end">
        <CircularProgress />
      </InputAdornment>
    );
  }, [isLoading]);

  const showRecentRecords = useMemo(
    () => term.length === 0 && recentRecords.length > 0 && results.length === 0,
    [term.length, recentRecords.length, results.length]
  );

  const searchInputHelperText = useMemo(() => {
    if (isNoResultsFound) return 'No results found';
    if (term.length > 0 && term.length < 3) return 'Minimum 3 characters required';
    return '';
  }, [term.length, isNoResultsFound]);

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={closeHandler}>
        <DialogTitle
          component="form"
          display="flex"
          gap={1}
          alignItems="center"
          onSubmit={onSubmitHandler}
        >
          <TextField
            size="medium"
            value={term}
            fullWidth
            variant="standard"
            placeholder="Search..."
            autoFocus
            helperText={searchInputHelperText}
            onChange={({ target }) => setTerm(target.value)}
            slotProps={{
              input: {
                disableUnderline: true,
                startAdornment: startAdornment,
                endAdornment: endAdornment,
                sx: { height: '42px', fontSize: '1.5rem' },
              },
            }}
            sx={{ height: '42px' }}
          />
          <Chip
            label="Exit"
            sx={{ borderRadius: '6px', fontSize: '1.05rem' }}
            clickable
            onClick={() => closeHandler()}
          />
        </DialogTitle>
        {showRecentRecords && (
          <Divider variant="middle">
            <Chip label="Recent Records" size="small" />
          </Divider>
        )}
        {results.length > 0 && (
          <Divider variant="middle">
            <Chip label="Search Results" size="small" />
          </Divider>
        )}
        <DialogContent>
          {showRecentRecords && (
            <Box>
              <List>
                {recentRecords.map((item) => (
                  <Box
                    key={`${item.entityId}-${item.recordId}`}
                    sx={{
                      borderColor: (t) => t.palette.background.paper,
                      borderWidth: '1px',
                      borderStyle: 'dashed',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: (t) => t.palette.primary.main,
                        borderWidth: '1px',
                        borderStyle: 'dashed',
                        backgroundColor: `rgba()`,
                      },
                    }}
                  >
                    <ListItem
                      disableGutters
                      disablePadding
                      secondaryAction={
                        <Chip
                          avatar={<Avatar>{entityIcon(item.entityId)}</Avatar>}
                          label={entityName(item.entityId)}
                          sx={{ mr: 2, borderRadius: '6px' }}
                        />
                      }
                    >
                      <Link
                        to={recordLink(item.entityId, item.recordId)}
                        //target="_blank"
                        style={{ color: 'inherit', textDecoration: 'none', flexGrow: 1 }}
                      >
                        <ListItemButton onClick={() => closeHandler()}>
                          <ListItemText
                            primary={item.title}
                            secondary={recentRecordsSecondaryText(item)}
                            slotProps={{ primary: { variant: 'subtitle2' } }}
                          />
                        </ListItemButton>
                      </Link>
                    </ListItem>
                  </Box>
                ))}
              </List>
            </Box>
          )}
          <List>
            {results.map((item) => (
              <Box
                key={`${item.recordType}-${item.recordId}`}
                sx={{
                  borderColor: (t) => t.palette.background.paper,
                  borderWidth: '1px',
                  borderStyle: 'dashed',
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: (t) => t.palette.primary.main,
                    borderWidth: '1px',
                    borderStyle: 'dashed',
                    backgroundColor: `rgba()`,
                  },
                }}
              >
                <ListItem
                  disableGutters
                  disablePadding
                  secondaryAction={
                    <Chip
                      avatar={<Avatar>{entityIcon(item.recordType)}</Avatar>}
                      label={entityName(item.recordType)}
                      sx={{ mr: 2, borderRadius: '6px' }}
                    />
                  }
                >
                  <Link
                    to={recordLink(item.recordType, item.recordId)}
                    //target="_blank"
                    style={{ color: 'inherit', textDecoration: 'none', flexGrow: 1 }}
                  >
                    <ListItemButton onClick={() => closeHandler()}>
                      <ListItemText
                        primary={item.recordName}
                        secondary={secondaryText(item)}
                        slotProps={{
                          primary: { variant: 'subtitle2' },
                          secondary: { component: 'div' },
                        }}
                      />
                    </ListItemButton>
                  </Link>
                </ListItem>
              </Box>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
}
